import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Row,
  Col,
  Space,
  Card,
  Button,
  Image,
  Breadcrumb,
  Table,
  Form,
  Steps,
  Input,
  Select,
  DatePicker,
} from "antd";
import * as yup from "yup";
import { Title } from "../../LandingPage/Components/Title/Title";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import { useNotification } from "~/Hooks/useNotification";
import moment from "moment";
import SuccessModal from "~/Components/Modal/SuccessModal";
import { WrapperCard } from "~/Components/WrapperCard/WrapperCard";
import { stageList, sectorList, subSector, countryList } from "./mock";
import { ContentWrapper } from "~/Components/Layout/ContentWrapper";

interface FormItems {
  projectName: string;
  projectDescription: string;
  sector: string;
  subSector: string;
  country: string;
  city: string;
  estimatedProjectCost: number;
  projectStage: string;
  stageDescription: string;
  expectedContractSigningDate: string;
  expectedFinancialCloseDate: string;
  expectedProjectConstructionStartDate: string;
  expectedProjectOperationStartDate: string;
  projectOwner: string;
  contactPersonName: string;
  contactPersonEmail: string;
}

const defaultValue = {
  projectName: "",
  projectDescription: "",
  sector: "",
  subSector: "",
  country: "",
  city: "",
  estimatedProjectCost: 0,
  projectStage: "",
  stageDescription: "",
  expectedContractSigningDate: "",
  expectedFinancialCloseDate: "",
  expectedProjectConstructionStartDate: "",
  expectedProjectOperationStartDate: "",
  projectOwner: "",
  contactPersonEmail: "",
  contactPersonName: "",
} as FormItems;

const EqualCol = ({ children }: { children: ReactNode }) => (
  <Col xs={24} md={12}>
    {children}
  </Col>
);

const getItemText = (name: string, value: any, isBoolean?: any) => {
  return (
    <Space direction="vertical" style={{ marginBottom: 20 }}>
      <Typography.Text
        type="secondary"
        style={{ fontSize: 14, fontWeight: 500 }}
      >
        {name}
      </Typography.Text>
      <Typography.Text style={{ fontSize: 16 }}>
        {!isBoolean ? value : value ? "Enabled" : "Disabled"}
      </Typography.Text>
    </Space>
  );
};

export const NewProject = () => {
  const [initialValue, setInitialValue] = useState<FormItems>(defaultValue);
  const [showSuccess, setShowSuccess] = useState(false);
  const { openNotification } = useNotification();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  useEffect(() => {}, []);

  const onSubmit = async (values: any) => {
    // openNotification({
    //   title: "Success",
    //   message: `Template updated successfully`,
    //   type: "success",
    // });
    setShowSuccess(true);
  };

  const stepItems: any = [
    {
      key: 0,
      item: "Basic Info",
      title: "Basic Info",
      icon: <Icon icon={currentStep > 0 ? "charm:tick" : "mdi:form"} />,
    },
    {
      key: 1,
      item: "Contract Details",
      title: "Contract Details",
      icon: (
        <Icon
          icon={currentStep > 0 ? "charm:tick" : "mdi:file-document-outline"}
        />
      ),
    },
    {
      key: 2,
      item: "Owner Details",
      title: "Owner Details",
      icon: <Icon icon={currentStep > 1 ? "charm:tick" : "mdi:file-sign"} />,
    },
    {
      key: 3,
      item: "Review & Submit",
      title: "Review & Submit",
      icon: (
        <Icon
          icon={currentStep > 3 ? "charm:tick" : "mdi:application-edit-outline"}
        />
      ),
    },
  ];

  const validationSchema1 = yup.object().shape({
    projectName: yup
      .string()
      .trim("Project Name cannot include leading and trailing spaces")
      .strict(true)
      .required("Project Name required"),
    projectDescription: yup
      .string()
      .trim("Project Description cannot include leading and trailing spaces")
      .strict(true)
      .required("Project Description required"),
    sector: yup.string().strict(true).required("Sector required"),
    subSector: yup.string().strict(true).required("Sub Sector required"),
    country: yup.string().strict(true).required("Country required"),
    city: yup
      .string()
      .trim("City of project cannot include leading and trailing spaces")
      .strict(true)
      .required("City of project required"),
  });

  const validationSchema2 = yup.object().shape({
    estimatedProjectCost: yup
      .number()
      .integer()
      .min(1, "Estimated project cost must be greater than or equal to 1")
      .required("Estimated project cost required"),
    projectStage: yup.string().strict(true).required("Project Stage required"),
    stageDescription: yup
      .string()
      .trim("Stage Description cannot include leading and trailing spaces")
      .strict(true)
      .required("Stage Description required"),
    expectedContractSigningDate: yup
      .string()
      .required("Expected contract signing date required"),
    expectedFinancialCloseDate: yup
      .string()
      .required("Expected financial close date required"),
    expectedProjectConstructionStartDate: yup
      .string()
      .required("Expected project construction start date required"),
    expectedProjectOperationStartDate: yup
      .string()
      .required("Expected project operation start date required"),
  });

  const validationSchema3 = yup.object().shape({
    projectOwner: yup
      .string()
      .trim("Project owner name cannot include leading and trailing spaces")
      .strict(true)
      .required("Project owner name required"),
    contactPersonName: yup
      .string()
      .trim("Contact person name cannot include leading and trailing spaces")
      .strict(true)
      .required("Contact person name required"),
    contactPersonEmail: yup
      .string()
      .lowercase("Email address must be a lowercase")
      .strict(true)
      .email("Email address must be a valid email")
      .required("Contract Personl Email required"),
  });

  const validationSchema = useMemo(() => {
    if (currentStep === 0) {
      return validationSchema1;
    } else if (currentStep === 1) {
      return validationSchema2;
    } else if (currentStep === 2) {
      return validationSchema3;
    }
  }, [currentStep]);

  return (
    <>
      <ContentWrapper
        loading={false}
        // customTitle={
        //   <div>
        //     <Title title="NEW PRODUCT" width={340} variant={2} />
        //   </div>
        // }
      >
        <SuccessModal
          open={showSuccess}
          onClose={() => navigate("/investment-hub")}
          content={
            <Typography.Text style={{ textAlign: "center" }}>
              New Project added successfully
              {/* <b> {name}</b> */}
            </Typography.Text>
          }
        />
        <Row className="add-new-project">
          {/* <Col lg={2}></Col> */}
          <Col span={24}>
            <Title title="NEW PROJECT" width={340} variant={2} />
          </Col>
          <Col lg={24}>
            <div>
              <Formik
                initialValues={initialValue}
                onSubmit={(values) => onSubmit(values)}
                validationSchema={validationSchema}
              >
                {(props) => {
                  const { values } = props;
                  return (
                    <>
                      <Form layout="vertical" onFinish={props.handleSubmit}>
                        <Steps
                          current={currentStep}
                          items={stepItems}
                          labelPlacement="vertical"
                          size="default"
                          responsive
                        />
                        <Row style={{ marginTop: 16 }}>
                          <Col xs={24} md={currentStep === 3 ? 24 : 16}>
                            {currentStep === 0 && (
                              <>
                                <div>
                                  <Typography.Title
                                    level={4}
                                    style={{
                                      marginBottom: 12,
                                    }}
                                  >
                                    Basic Project Details
                                  </Typography.Title>
                                </div>

                                <Row gutter={[32, 0]} align="top">
                                  <Col span={24}>
                                    <Form.Item label="Project Name">
                                      <Field name={`projectName`}>
                                        {({ field }: FieldProps) => (
                                          <Input {...field} />
                                        )}
                                      </Field>
                                      <Typography.Text type="danger">
                                        <ErrorMessage name={`projectName`} />
                                      </Typography.Text>
                                    </Form.Item>
                                  </Col>
                                  <Col span={24}>
                                    <Form.Item label="Project Description">
                                      <Field name={`projectDescription`}>
                                        {({ field }: FieldProps) => (
                                          <Input.TextArea {...field} />
                                        )}
                                      </Field>
                                      <Typography.Text type="danger">
                                        <ErrorMessage
                                          name={`projectDescription`}
                                        />
                                      </Typography.Text>
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item label={"Sector"}>
                                      <Field name={`sector`}>
                                        {({ field }: FieldProps) => (
                                          <Select
                                            value={field.value}
                                            onBlur={() =>
                                              props.setFieldTouched(
                                                "sector",
                                                true
                                              )
                                            }
                                            onChange={(e) => {
                                              props.setFieldValue("sector", e);
                                              props.setFieldValue(
                                                "subSector",
                                                ""
                                              );
                                            }}
                                            status={
                                              (props.touched.sector &&
                                                props.errors.sector &&
                                                "error") ||
                                              ""
                                            }
                                          >
                                            {sectorList.map((sector: any) => (
                                              <Select.Option
                                                value={sector.id}
                                                key={sector.id}
                                              >
                                                {sector.name}
                                              </Select.Option>
                                            ))}
                                          </Select>
                                        )}
                                      </Field>
                                      <Typography.Text type="danger">
                                        <ErrorMessage name={`sector`} />
                                      </Typography.Text>
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item label={"Sub Sector"}>
                                      <Field name={`subSector`}>
                                        {({ field }: FieldProps) => (
                                          <Select
                                            disabled={!values.sector}
                                            value={field.value}
                                            onBlur={() =>
                                              props.setFieldTouched(
                                                "subSector",
                                                true
                                              )
                                            }
                                            onChange={(e) => {
                                              props.setFieldValue(
                                                "subSector",
                                                e
                                              );
                                            }}
                                            status={
                                              (props.touched.subSector &&
                                                props.errors.subSector &&
                                                "error") ||
                                              ""
                                            }
                                          >
                                            {subSector[values.sector] &&
                                              subSector[values.sector].map(
                                                (sector: any) => (
                                                  <Select.Option
                                                    value={sector.id}
                                                    key={sector.id}
                                                  >
                                                    {sector.name}
                                                  </Select.Option>
                                                )
                                              )}
                                          </Select>
                                        )}
                                      </Field>
                                      <Typography.Text type="danger">
                                        <ErrorMessage name={`subSector`} />
                                      </Typography.Text>
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item label={"Country"}>
                                      <Field name={`country`}>
                                        {({ field }: FieldProps) => (
                                          <Select
                                            value={field.value}
                                            onBlur={() =>
                                              props.setFieldTouched(
                                                "country",
                                                true
                                              )
                                            }
                                            onChange={(e) => {
                                              props.setFieldValue("country", e);
                                            }}
                                            status={
                                              (props.touched.country &&
                                                props.errors.country &&
                                                "error") ||
                                              ""
                                            }
                                          >
                                            {countryList.map((country: any) => (
                                              <Select.Option
                                                value={country.id}
                                                key={country.id}
                                              >
                                                {country.name}
                                              </Select.Option>
                                            ))}
                                          </Select>
                                        )}
                                      </Field>
                                      <Typography.Text type="danger">
                                        <ErrorMessage name={`country`} />
                                      </Typography.Text>
                                    </Form.Item>
                                  </Col>
                                  <Col span={12}>
                                    <Form.Item label="Project City">
                                      <Field name={`city`}>
                                        {({ field }: FieldProps) => (
                                          <Input {...field} />
                                        )}
                                      </Field>
                                      <Typography.Text type="danger">
                                        <ErrorMessage name={`city`} />
                                      </Typography.Text>
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </>
                            )}
                            {currentStep === 1 && (
                              <Row style={{ marginBottom: 16 }}>
                                <Col xs={24} md={24}>
                                  <Typography.Title
                                    level={4}
                                    style={{ marginBottom: 20 }}
                                  >
                                    Contract Details
                                  </Typography.Title>
                                </Col>

                                <Col span={24}>
                                  <Row gutter={[32, 0]}>
                                    <Col span={12}>
                                      <Form.Item label="Estimated Project Cost (USD in Million)">
                                        <Field name={`estimatedProjectCost`}>
                                          {({ field }: FieldProps) => (
                                            <Input type="number" {...field} />
                                          )}
                                        </Field>
                                        <Typography.Text type="danger">
                                          <ErrorMessage
                                            name={`estimatedProjectCost`}
                                          />
                                        </Typography.Text>
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item label={"Project Stage"}>
                                        <Field name={`projectStage`}>
                                          {({ field }: FieldProps) => (
                                            <Select
                                              value={field.value}
                                              onBlur={() =>
                                                props.setFieldTouched(
                                                  "projectStage",
                                                  true
                                                )
                                              }
                                              onChange={(e) => {
                                                props.setFieldValue(
                                                  "projectStage",
                                                  e
                                                );
                                              }}
                                              status={
                                                (props.touched.projectStage &&
                                                  props.errors.projectStage &&
                                                  "error") ||
                                                ""
                                              }
                                            >
                                              {stageList.map((stage: any) => (
                                                <Select.Option
                                                  value={stage.id}
                                                  key={stage.id}
                                                >
                                                  {stage.name}
                                                </Select.Option>
                                              ))}
                                            </Select>
                                          )}
                                        </Field>
                                        <Typography.Text type="danger">
                                          <ErrorMessage name={`projectStage`} />
                                        </Typography.Text>
                                      </Form.Item>
                                    </Col>
                                    <Col span={24}>
                                      <Form.Item label="Project Stage Description">
                                        <Field name={`stageDescription`}>
                                          {({ field }: FieldProps) => (
                                            <Input.TextArea {...field} />
                                          )}
                                        </Field>
                                        <Typography.Text type="danger">
                                          <ErrorMessage
                                            name={`stageDescription`}
                                          />
                                        </Typography.Text>
                                      </Form.Item>
                                    </Col>

                                    <Col span={12}>
                                      <Field name="expectedContractSigningDate">
                                        {({ field }: FieldProps) => {
                                          return (
                                            <Form.Item label="Expected Contract Signing Date">
                                              <DatePicker
                                                allowClear
                                                onBlur={() =>
                                                  props.setFieldTouched(
                                                    "expectedContractSigningDate",
                                                    true
                                                  )
                                                }
                                                status={
                                                  (props.touched
                                                    .expectedContractSigningDate &&
                                                    props.errors
                                                      .expectedContractSigningDate &&
                                                    "error") ||
                                                  ""
                                                }
                                                picker="month"
                                                onChange={(e, dateString) => {
                                                  props.setFieldValue(
                                                    `expectedContractSigningDate`,
                                                    dateString
                                                  );
                                                }}
                                                format="MMM YYYY"
                                                {...(field.value && {
                                                  value: moment(
                                                    field.value,
                                                    "MMM YYYY"
                                                  ),
                                                })}
                                                style={{ width: "100%" }}
                                              />
                                              {props.touched
                                                .expectedContractSigningDate &&
                                                props.errors
                                                  .expectedContractSigningDate && (
                                                  <Typography.Text type="danger">
                                                    {
                                                      props.errors
                                                        .expectedContractSigningDate
                                                    }
                                                  </Typography.Text>
                                                )}
                                            </Form.Item>
                                          );
                                        }}
                                      </Field>
                                    </Col>
                                    <Col span={12}>
                                      <Field name="expectedFinancialCloseDate">
                                        {({ field }: FieldProps) => {
                                          return (
                                            <Form.Item label="Expected Financial Close Date">
                                              <DatePicker
                                                allowClear
                                                onBlur={() =>
                                                  props.setFieldTouched(
                                                    "expectedFinancialCloseDate",
                                                    true
                                                  )
                                                }
                                                status={
                                                  (props.touched
                                                    .expectedFinancialCloseDate &&
                                                    props.errors
                                                      .expectedFinancialCloseDate &&
                                                    "error") ||
                                                  ""
                                                }
                                                picker="month"
                                                onChange={(e, dateString) => {
                                                  props.setFieldValue(
                                                    `expectedFinancialCloseDate`,
                                                    dateString
                                                  );
                                                }}
                                                format="MMM YYYY"
                                                {...(field.value && {
                                                  value: moment(
                                                    field.value,
                                                    "MMM YYYY"
                                                  ),
                                                })}
                                                style={{ width: "100%" }}
                                              />
                                              {props.touched
                                                .expectedFinancialCloseDate &&
                                                props.errors
                                                  .expectedFinancialCloseDate && (
                                                  <Typography.Text type="danger">
                                                    {
                                                      props.errors
                                                        .expectedFinancialCloseDate
                                                    }
                                                  </Typography.Text>
                                                )}
                                            </Form.Item>
                                          );
                                        }}
                                      </Field>
                                    </Col>
                                    <Col span={12}>
                                      <Field name="expectedProjectConstructionStartDate">
                                        {({ field }: FieldProps) => {
                                          return (
                                            <Form.Item label="Expected Project Construction Start Date">
                                              <DatePicker
                                                allowClear
                                                onBlur={() =>
                                                  props.setFieldTouched(
                                                    "expectedProjectConstructionStartDate",
                                                    true
                                                  )
                                                }
                                                status={
                                                  (props.touched
                                                    .expectedProjectConstructionStartDate &&
                                                    props.errors
                                                      .expectedProjectConstructionStartDate &&
                                                    "error") ||
                                                  ""
                                                }
                                                picker="month"
                                                onChange={(e, dateString) => {
                                                  props.setFieldValue(
                                                    `expectedProjectConstructionStartDate`,
                                                    dateString
                                                  );
                                                }}
                                                format="MMM YYYY"
                                                {...(field.value && {
                                                  value: moment(
                                                    field.value,
                                                    "MMM YYYY"
                                                  ),
                                                })}
                                                style={{ width: "100%" }}
                                              />
                                              {props.touched
                                                .expectedProjectConstructionStartDate &&
                                                props.errors
                                                  .expectedProjectConstructionStartDate && (
                                                  <Typography.Text type="danger">
                                                    {
                                                      props.errors
                                                        .expectedProjectConstructionStartDate
                                                    }
                                                  </Typography.Text>
                                                )}
                                            </Form.Item>
                                          );
                                        }}
                                      </Field>
                                    </Col>
                                    <Col span={12}>
                                      <Field name="expectedProjectOperationStartDate">
                                        {({ field }: FieldProps) => {
                                          return (
                                            <Form.Item label="Expected Project Operation Start Date">
                                              <DatePicker
                                                allowClear
                                                onBlur={() =>
                                                  props.setFieldTouched(
                                                    "expectedProjectOperationStartDate",
                                                    true
                                                  )
                                                }
                                                status={
                                                  (props.touched
                                                    .expectedProjectOperationStartDate &&
                                                    props.errors
                                                      .expectedProjectOperationStartDate &&
                                                    "error") ||
                                                  ""
                                                }
                                                picker="month"
                                                onChange={(e, dateString) => {
                                                  props.setFieldValue(
                                                    `expectedProjectOperationStartDate`,
                                                    dateString
                                                  );
                                                }}
                                                format="MMM YYYY"
                                                {...(field.value && {
                                                  value: moment(
                                                    field.value,
                                                    "MMM YYYY"
                                                  ),
                                                })}
                                                style={{ width: "100%" }}
                                              />
                                              {props.touched
                                                .expectedProjectOperationStartDate &&
                                                props.errors
                                                  .expectedProjectOperationStartDate && (
                                                  <Typography.Text type="danger">
                                                    {
                                                      props.errors
                                                        .expectedProjectOperationStartDate
                                                    }
                                                  </Typography.Text>
                                                )}
                                            </Form.Item>
                                          );
                                        }}
                                      </Field>
                                    </Col>
                                  </Row>
                                </Col>
                              </Row>
                            )}
                            {currentStep === 2 && (
                              <>
                                <div
                                  style={{
                                    width: "100%",
                                    marginBottom: 16,
                                  }}
                                >
                                  <Typography.Title
                                    level={4}
                                    style={{ marginBottom: 10 }}
                                  >
                                    Owner Details
                                  </Typography.Title>
                                  <Row gutter={[32, 0]}>
                                    <Col span={12}>
                                      <Form.Item label="Project Owner Name">
                                        <Field name={`projectOwner`}>
                                          {({ field }: FieldProps) => (
                                            <Input {...field} />
                                          )}
                                        </Field>
                                        <Typography.Text type="danger">
                                          <ErrorMessage name={`projectOwner`} />
                                        </Typography.Text>
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item label="Contact Person Name">
                                        <Field name={`contactPersonName`}>
                                          {({ field }: FieldProps) => (
                                            <Input {...field} />
                                          )}
                                        </Field>
                                        <Typography.Text type="danger">
                                          <ErrorMessage
                                            name={`contactPersonName`}
                                          />
                                        </Typography.Text>
                                      </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                      <Form.Item label="Contact Person Email">
                                        <Field name={`contactPersonEmail`}>
                                          {({ field }: FieldProps) => (
                                            <Input {...field} />
                                          )}
                                        </Field>
                                        <Typography.Text type="danger">
                                          <ErrorMessage
                                            name={`contactPersonEmail`}
                                          />
                                        </Typography.Text>
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </div>
                              </>
                            )}
                            {currentStep === 3 && (
                              <>
                                <Row style={{ marginBottom: 20 }}>
                                  <Col span={24} style={{ marginBottom: 20 }}>
                                    <WrapperCard
                                      title="Basic Project Details"
                                      smallHeader
                                      action={
                                        <Icon
                                          icon="carbon:edit"
                                          style={{
                                            color: "#fd349c",
                                            cursor: "pointer",
                                            fontSize: 20,
                                            verticalAlign: "middle",
                                          }}
                                          onClick={() => setCurrentStep(0)}
                                        />
                                      }
                                    >
                                      <Row>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Project Name",
                                            props.values.projectName
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Project Description",
                                            props.values.projectDescription
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Sector",
                                            props.values.sector
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Sub Sector",
                                            props.values.subSector
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Country",
                                            props.values.country
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "City",
                                            props.values.city
                                          )}
                                        </Col>
                                      </Row>
                                      {/* <Row>
                                        <Col xs={24} md={24}>
                                          <Typography.Title
                                            level={5}
                                            style={{ margin: 4 }}
                                          >
                                            Expiration
                                          </Typography.Title>
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Template Name",
                                            props.values.projectName
                                          )}
                                        </Col>
                                      </Row> */}
                                    </WrapperCard>
                                  </Col>
                                  <Col span={24} style={{ marginBottom: 20 }}>
                                    <WrapperCard
                                      title="Contract Details"
                                      smallHeader
                                      action={
                                        <Icon
                                          icon="carbon:edit"
                                          style={{
                                            color: "#fd349c",
                                            cursor: "pointer",
                                            fontSize: 20,
                                            verticalAlign: "middle",
                                          }}
                                          onClick={() => setCurrentStep(1)}
                                        />
                                      }
                                    >
                                      <Row>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Estimated Project Cost",
                                            `USD ${props.values.estimatedProjectCost} Million`
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Project Stage",
                                            props.values.projectStage
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Stage Description",
                                            props.values.stageDescription
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Expected Contract Signing Date",
                                            props.values
                                              .expectedContractSigningDate
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Expected Financial Close Date",
                                            props.values
                                              .expectedFinancialCloseDate
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Expected Project Construction Start Date",
                                            props.values
                                              .expectedProjectConstructionStartDate
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Expected Project Operation Start Date",
                                            props.values
                                              .expectedProjectOperationStartDate
                                          )}
                                        </Col>
                                      </Row>
                                    </WrapperCard>
                                  </Col>
                                  <Col span={24} style={{ marginBottom: 20 }}>
                                    <WrapperCard
                                      title="Owner Details"
                                      smallHeader
                                      action={
                                        <Icon
                                          icon="carbon:edit"
                                          style={{
                                            color: "#fd349c",
                                            cursor: "pointer",
                                            fontSize: 20,
                                            verticalAlign: "middle",
                                          }}
                                          onClick={() => setCurrentStep(2)}
                                        />
                                      }
                                    >
                                      <Row>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Project Owner",
                                            props.values.projectOwner
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Contact Person Name",
                                            props.values.contactPersonName
                                          )}
                                        </Col>
                                        <Col xs={24} md={8}>
                                          {getItemText(
                                            "Contact Person Email",
                                            props.values.contactPersonEmail
                                          )}
                                        </Col>
                                      </Row>
                                    </WrapperCard>
                                  </Col>
                                </Row>
                              </>
                            )}
                            <Row>
                              <Col
                                xs={24}
                                md={24}
                                style={{
                                  textAlign: "right",
                                }}
                              >
                                {currentStep === 3 && (
                                  <Space>
                                    <Button
                                      type="default"
                                      onClick={() =>
                                        setCurrentStep((current) => current - 1)
                                      }
                                      style={{ marginBottom: 80 }}
                                    >
                                      Back
                                    </Button>
                                    <Button
                                      htmlType="submit"
                                      type="primary"
                                      // loading={isSubmitLoading}
                                      disabled={!props.isValid}
                                      style={{ marginBottom: 80 }}
                                    >
                                      Submit
                                    </Button>
                                  </Space>
                                )}

                                {currentStep < 3 && (
                                  <Space>
                                    {currentStep > 0 && (
                                      <Button
                                        type="default"
                                        onClick={() =>
                                          setCurrentStep(
                                            (current) => current - 1
                                          )
                                        }
                                      >
                                        Back
                                      </Button>
                                    )}
                                    <Button
                                      type="primary"
                                      disabled={!(props.isValid && props.dirty)}
                                      onClick={() =>
                                        setCurrentStep((current) => current + 1)
                                      }
                                    >
                                      Next
                                    </Button>
                                  </Space>
                                )}
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </div>
          </Col>
          <Col lg={2}></Col>
        </Row>
      </ContentWrapper>
    </>
  );
};
